import React, { useState, useRef, useCallback, useEffect} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { api } from '../config';
import "./ItineraryPage.css";

 // Time layout constants
 const TIME_START = 0;
 const TIME_END = 48;
 const GRID_HEIGHT = 25; // 30 mins = 25px (1 hour = 50px)
 const QUICK_EDIT_PANEL_EST_HEIGHT = 120;
 const CALENDAR_CONTENT_HEIGHT = 2400;
 function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of earth in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function estimateTravelTimeInMinutes(distanceKm) {
  const averageSpeedKmph = 30;
  return Math.round((distanceKm / averageSpeedKmph) * 60);
}
function formatTravelTime(value) {
  if (typeof value === "string") return value;

  const mins = Math.round(Number(value) || 0);

  if (mins < 60) return `${mins} min`;

  const days = Math.floor(mins / 1440);
  const remainingMinutes = mins % 1440;
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;

  if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} ${hours} hr ${minutes} min`;
  }

  return `${hours} hr ${minutes} min`;
}
function getTravelTimeBetween(from, to) {
  if (!from?.coordinates || !to?.coordinates) return 0;
  const dist = haversineDistance(
    from.coordinates.latitude,
    from.coordinates.longitude,
    to.coordinates.latitude,
    to.coordinates.longitude
  );
  return estimateTravelTimeInMinutes(dist);
}
function getPlaceKey(place) {
  return String(
    place?.id ??
      place?.place_id ??
      place?.google_place_id ??
      place?.placeId ??
      `${place?.name || ""}-${place?.address || ""}`
  );
}

function formatScheduleTime(hour) {
  if (!Number.isFinite(hour)) return "";

  const totalMinutes = Math.round(hour * 60);
  const normalizedMinutes = Math.max(0, Math.min(24 * 60, totalMinutes));

  if (normalizedMinutes >= 24 * 60) {
    return "12:00 AM";
  }

  const wholeHour = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;

  const period = wholeHour >= 12 ? "PM" : "AM";
  const displayHour =
    wholeHour % 12 === 0 ? 12 : wholeHour % 12;

  return `${displayHour}:${String(minutes).padStart(2, "0")} ${period}`;
}

/**
 * Recalculate travel times while preserving every
 * attraction's existing user-selected start time.
 */
function recalculateTravelTimesPreserveTimes(places) {
  const updated = places
    .map((place) => ({ ...place }))
    .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));

  if (!updated.length) {
    return updated;
  }

  updated[0].travel_time_from_previous = "0 min";

  for (let i = 1; i < updated.length; i++) {
    const previous = updated[i - 1];
    const current = updated[i];

    const travelMinutes =
      getTravelTimeBetween(previous, current);

    current.travel_time_from_previous =
      formatTravelTime(travelMinutes);
  }

  return updated;
}

/**
 * Validate a complete manually edited day.
 *
 * It:
 * - preserves selected start times
 * - recalculates all travel times
 * - prevents overlaps
 * - ensures enough travel time
 * - prevents anything ending after midnight
 */
function prepareAndValidateDay(places) {
  let updated = places
    .map((place) => ({ ...place }))
    .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));

  for (const place of updated) {
    const start = Number(place.start_time);
    const duration = Number(place.visit_duration);

    if (
      !Number.isFinite(start) ||
      !Number.isFinite(duration)
    ) {
      return {
        ok: false,
        message:
          "One of the attractions has an invalid start time or duration.",
      };
    }

    if (start < 0 || start >= 24) {
      return {
        ok: false,
        message:
          "Please choose a valid start time within the selected day.",
      };
    }

    if (duration < 0.5) {
      return {
        ok: false,
        message:
          "Attraction duration must be at least 30 minutes.",
      };
    }

    if (start + duration > 24 + 1e-6) {
      return {
        ok: false,
        message: `${place.name} would end after midnight. Please choose an earlier start time or a shorter duration.`,
      };
    }
  }

  updated =
    recalculateTravelTimesPreserveTimes(updated);

  for (let i = 1; i < updated.length; i++) {
    const previous = updated[i - 1];
    const current = updated[i];

    const previousEnd =
      previous.start_time +
      previous.visit_duration;

    const travelMinutes =
      getTravelTimeBetween(previous, current);

    const earliestStart =
      previousEnd + travelMinutes / 60;

    if (
      current.start_time <
      earliestStart - 1e-6
    ) {
      return {
        ok: false,
        message:
          `${previous.name} ends at ${formatScheduleTime(previousEnd)}. ` +
          `Estimated travel time to ${current.name} is ${formatTravelTime(travelMinutes)}. ` +
          `${current.name} must start at ${formatScheduleTime(earliestStart)} or later.`,
      };
    }
  }

  return {
    ok: true,
    places: updated,
  };
}

/**
 * Duration changes work differently from manual moves.
 *
 * Keep the edited attraction fixed and automatically
 * move only attractions after it.
 */
function reflowFollowingPlaces(
  places,
  anchorKey
) {
  let updated = places
    .map((place) => ({ ...place }))
    .sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));

  let anchorIndex = updated.findIndex(
    (place) =>
      getPlaceKey(place) === anchorKey
  );

  if (anchorIndex < 0) {
    return {
      ok: false,
      message: "Unable to update this attraction.",
    };
  }

  /*
   * Recalculate current travel relationships first.
   */
  updated =
    recalculateTravelTimesPreserveTimes(updated);

  anchorIndex = updated.findIndex(
    (place) =>
      getPlaceKey(place) === anchorKey
  );

  /*
   * Only attractions AFTER the edited attraction move.
   */
  for (
    let i = anchorIndex + 1;
    i < updated.length;
    i++
  ) {
    const previous = updated[i - 1];
    const current = updated[i];

    const travelMinutes =
      getTravelTimeBetween(previous, current);

    current.travel_time_from_previous =
      formatTravelTime(travelMinutes);

    const earliestStart =
      previous.start_time +
      previous.visit_duration +
      travelMinutes / 60;

    /*
     * Website time selectors use 30-minute intervals.
     * Round UP so travel time is never underestimated.
     */
    current.start_time =
      Math.ceil((earliestStart - 1e-9) * 2) / 2;

    if (
      current.start_time +
        current.visit_duration >
      24 + 1e-6
    ) {
      return {
        ok: false,
        message:
          "This duration would push the remaining itinerary past midnight. Please choose a shorter duration.",
      };
    }
  }

  /*
   * Final travel recalculation after all positions settle.
   */
  updated =
    recalculateTravelTimesPreserveTimes(updated);

  return {
    ok: true,
    places: updated,
  };
}
function recalculateTimesForDay(attractions) {
  const updated = [];
  for (let i = 0; i < attractions.length; i++) {
    const current = { ...attractions[i] };
    if (i === 0) {
      current.start_time = 10;
      current.travel_time_from_previous = "0 min";
    } else {
      const prev = updated[i - 1];
      const travel = getTravelTimeBetween(prev, current);
      current.travel_time_from_previous = formatTravelTime(travel);
      current.start_time = prev.start_time + prev.visit_duration + travel / 60;
      current.start_time = Math.round(current.start_time * 2) / 2;
    }
    updated.push(current);
  }
  return updated;
}

 const AttractionCard = ({ place, index, dayIndex, top, height, moveCard, totalDays, editableItinerary,setEditableItinerary, onDelete, setUndoHistory, isEditMode }) => {
  const [selectedDay, setSelectedDay] = useState(dayIndex);
  const [selectedTime, setSelectedTime] = useState(place.start_time);
  const [selectedDuration, setSelectedDuration] = useState(place.visit_duration);
  const scrollRef = useRef(null);
  useEffect(() => {
    setSelectedTime(place.start_time);
  }, [place.start_time]);
  useEffect(() => {
    setSelectedDuration(place.visit_duration ?? 2);
  }, [place.visit_duration]);
  
  useEffect(() => {
    setSelectedDay(dayIndex);
  }, [dayIndex]);
  useEffect(() => {
    if (scrollRef.current) {
      const gridHeight = 50; // px height per half-hour row
      const scrollToIndex = 20; // 10:00 AM = 20 × 30-minute blocks
      scrollRef.current.scrollTop = scrollToIndex * gridHeight;
    }
  }, []);

  const [isQuickEditOpen, setIsQuickEditOpen] = useState(false);
  const quickEditWouldOverflowBottom =
  top +
    height +
    QUICK_EDIT_PANEL_EST_HEIGHT >
  CALENDAR_CONTENT_HEIGHT;
  // Auto-close the panel when leaving Edit mode
  useEffect(() => {
  if (!isEditMode) setIsQuickEditOpen(false);
 }, [isEditMode]);

  
 const handleMove = () => {
  if (
    selectedDay === dayIndex &&
    selectedTime === place.start_time
  ) {
    return;
  }

  const targetDay =
    editableItinerary[selectedDay];

  if (!targetDay) {
    alert("Please choose a valid day.");
    return;
  }

  /*
   * Same-city restriction stays.
   */
  const sourceCity =
    editableItinerary[dayIndex]?.city;

  if (
    sourceCity &&
    targetDay.city &&
    sourceCity !== targetDay.city
  ) {
    alert(
      "You cannot move an attraction to a different city."
    );

    return;
  }

  const newEnd =
    selectedTime +
    (place.visit_duration ?? 2);

  if (newEnd > 24 + 1e-6) {
    alert(
      `${place.name} would end after midnight. Please choose an earlier start time.`
    );

    return;
  }

  moveCard(
    dayIndex,
    selectedDay,
    index,
    place,
    selectedTime
  );

  setIsQuickEditOpen(false);
};
  const handleDurationChange = () => {
    if (
      selectedDuration ===
      place.visit_duration
    ) {
      return;
    }
  
    const fixedStart =
      place.start_time;
  
    const newEnd =
      fixedStart + selectedDuration;
  
    /*
     * Never move the edited attraction earlier just
     * because the new duration does not fit.
     */
    if (newEnd > 24 + 1e-6) {
      alert(
        `${place.name} would end after midnight. Please choose a shorter duration.`
      );
  
      return;
    }
  
    setEditableItinerary((prev) => {
      const next =
        JSON.parse(JSON.stringify(prev));
  
      const places =
        next[dayIndex]?.places;
  
      if (!places) {
        return prev;
      }
  
      const placeKey =
        getPlaceKey(place);
  
      const editedIndex =
        places.findIndex(
          (item) =>
            getPlaceKey(item) ===
            placeKey
        );
  
      if (editedIndex < 0) {
        return prev;
      }
  
      /*
       * Preserve exact start time.
       */
      places[editedIndex].start_time =
        fixedStart;
  
      places[editedIndex].visit_duration =
        selectedDuration;
  
      /*
       * Move ONLY attractions following this one.
       */
      const result =
        reflowFollowingPlaces(
          places,
          placeKey
        );
  
      if (!result.ok) {
        alert(result.message);
  
        return prev;
      }
  
      /*
       * Successful edit:
       * exactly one Undo snapshot.
       */
      setUndoHistory((history) => [
        ...history,
        JSON.parse(JSON.stringify(prev)),
      ]);
  
      next[dayIndex].places =
        result.places;
  
      return next;
    });
  
    setIsQuickEditOpen(false);
  };
  

  return (
    <div
  className={`attraction-card ${isQuickEditOpen ? 'qe-open' : ''}`}
  style={{
    top: `${top}px`,
    height: `${height}px`,
    zIndex: isQuickEditOpen ? 1000 : undefined,
  }}
>
    {isEditMode && (
  <button
    onClick={onDelete}
    className="delete-attraction-button"
    title="Delete Attraction"
  >
    ✕
  </button>
)}

<div className="attraction-details">
  <strong className="attraction-name">{place.name}</strong>
  <p className="attraction-address">{place.address}</p>
  <p className="attraction-duration">Duration: {place.visit_duration} hrs</p>
</div>

{/* Toggle only shows in Edit Mode */}
{isEditMode && (
  <button
    type="button"
    className="quick-edit-toggle"
    onClick={() => setIsQuickEditOpen(v => !v)}
    aria-expanded={isQuickEditOpen}
    aria-controls={`quick-edit-${place.id || `${dayIndex}-${index}`}`}
  >
    {isQuickEditOpen ? 'Close' : 'Quick Edit'}
  </button>
)}

{/* Quick Edit panel appears only when Edit Mode is on AND user opened it */}
{isEditMode && isQuickEditOpen && (
  <div
    id={`quick-edit-${place.id || `${dayIndex}-${index}`}`}
    className={`quick-edit-panel ${
      quickEditWouldOverflowBottom
        ? "quick-edit-panel-above"
        : ""
    }`}
  >
    {/* Duration row */}
    <div className="qe-row">
      <label className="qe-label">Duration</label>
      <select
        className="qe-select"
        value={selectedDuration}
        onChange={(e) => setSelectedDuration(parseFloat(e.target.value))}
      >
        {[...Array(12)].map((_, i) => {
          const val = (i + 1) * 0.5;
          return <option key={val} value={val}>{val} hrs</option>;
        })}
      </select>
      <button type="button" className="qe-apply" onClick={handleDurationChange}>
        Apply
      </button>
    </div>

    {/* Move row */}
    <div className="qe-row">
      <label className="qe-label">Move to</label>

      <select
        className="qe-select"
        value={selectedDay}
        onChange={(e) => setSelectedDay(parseInt(e.target.value))}
      >
        {Array.from({ length: totalDays }).map((_, i) => (
          <option key={i} value={i}>{`Day ${i + 1}`}</option>
        ))}
      </select>

      <select
        className="qe-select"
        value={selectedTime}
        onChange={(e) => setSelectedTime(parseFloat(e.target.value))}
      >
        {Array.from({ length: 48 }, (_, i) => {
          const hour = Math.floor(i / 2);
          const minutes = i % 2 === 0 ? '00' : '30';
          return <option key={i} value={i * 0.5}>{`${hour.toString().padStart(2,'0')}:${minutes}`}</option>;
        })}
      </select>

      <button type="button" className="qe-move" onClick={handleMove}>
        Move
      </button>
    </div>
  </div>
)}


    </div>
  );
};
  const DayColumn = ({ dayData, dayIndex, moveCard, allPlaces, totalDays, editableItinerary,setEditableItinerary, handleDeleteAttraction, setUndoHistory, isEditMode }) => {
    const columnRef = useRef();
    
    const gridRefs = useRef([]);
    
    // 🔷 1. Track used gridlines
    const occupiedGridlines = Array(TIME_END).fill(false); // 48 slots for 24 hrs (30 min each)
    allPlaces.forEach((place) => {
    const startIndex = Math.round(place.start_time * 2);
    const endIndex = Math.round((place.start_time + place.visit_duration) * 2);
    for (let i = startIndex; i < endIndex; i++) {
    occupiedGridlines[i] = true;
   }
   });
   
    return (
      <div className="calendar-column day-column" ref={columnRef}>
        <div className="cell day-header">
          <div className="day-number">
            Day {dayData.day} – {dayData.date}
          </div>
          <div className="day-city">{dayData.city}</div>
        </div>
        {/* Grid */}
        <div className="grid-background">
        {Array.from({ length: 49 }).map((_, i) => (
        <div
        key={i}
        className="grid-hour-line"
        data-grid-index={i}
        ref={(el) => (gridRefs.current[i] = el)}
       />
       ))}
        </div>
        {/* Cards */}
        {[...dayData.places]
  .sort((a, b) => a.start_time - b.start_time)
  .map((place, index) => {
    const snappedStart = place.start_time;
    const originalDuration = place.visit_duration;
    const top = (snappedStart + 0.5) * 4 * GRID_HEIGHT+ index * 10;;
    const height = originalDuration * 4 * GRID_HEIGHT;

    
    
    return (
      <React.Fragment key={place.id || `${place.name}-${index}`}>
        {index > 0 && (() => {
  const prevPlace = dayData.places[index - 1];
  const prevTop = (prevPlace.start_time + 0.5) * 4 * GRID_HEIGHT + (index - 1) * 10;
  const prevHeight = prevPlace.visit_duration * 4 * GRID_HEIGHT;

  const connectorTop = prevTop + prevHeight;
  const connectorBottom = top;
  const connectorHeight = connectorBottom - connectorTop;

  return (
    <>
      {/* Dotted vertical line */}
      <div
  className="dotted-vertical-line"
  style={{
    top: `${connectorTop}px`,
    height: `${connectorHeight}px`,
  }}
/>
      {/* Travel time label */}
      <div
  className="connector-label"
  style={{
    top: `${connectorTop + connectorHeight / 2 - 10}px`,
  }}
>

<div className="travel-time-box">
  <img 
    src="/icons/car-side-svgrepo-com.svg" 
    alt="Car Icon" 
    className="travel-time-icon" 
  />
  <span className="travel-time-text">
  {formatTravelTime(place.travel_time_from_previous)}
</span>
</div>

      </div>
    </>
  );
})()}
        <AttractionCard
          place={place}
          index={index}
          dayIndex={dayIndex}
          top={top}
          height={height}
          moveCard={moveCard}
          totalDays={totalDays}
          editableItinerary={editableItinerary}
          setEditableItinerary={setEditableItinerary}
          onDelete={() =>
            handleDeleteAttraction(
              dayIndex,
              getPlaceKey(place)
            )
          }
          setUndoHistory={setUndoHistory}
          isEditMode={isEditMode}
        />
      </React.Fragment>
    );
  })}

      </div>
    );
  };
  
function ItineraryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: savedIdFromUrl } = useParams();
  const savedId = location.state?.savedId || savedIdFromUrl || null;
  const [tripData, setTripData] = useState(location.state?.tripData || {});
  const [savedTitle, setSavedTitle] = useState(location.state?.savedTitle || '');
  const [isEditFromSaved, setIsEditFromSaved] = useState(Boolean(savedId));
const [exploreByCityRaw, setExploreByCityRaw] = useState(
  location.state?.explore || []
);
 const [isLoadingExplore, setIsLoadingExplore] = useState(false);
const [exploreError, setExploreError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [liveSearchResults, setLiveSearchResults] = useState([]);
  const [isSearchingLive, setIsSearchingLive] = useState(false);
  const { accessToken, isLoggedIn } = useAuth();
  // NEW: mobile tabs + selected day on mobile
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedDayMobile, setSelectedDayMobile] = useState(0);
  // scroll target for mobile (10:00)
 const tenAMRef = useRef(null);
 
  
  const calculateTotalDays = () => {
    if (!tripData.startDate || !tripData.endDate) return 0;
    const start = new Date(tripData.startDate);
    const end = new Date(tripData.endDate);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  };

  const totalDays = calculateTotalDays();

  const [daysPerDestination, setDaysPerDestination] = useState(() => {
    const cities = tripData.destinations || [];
    if (cities.length <= 1) return [totalDays];
    const baseDays = Math.floor(totalDays / cities.length);
    const daysArray = cities.map(() => baseDays);
    let remainingDays = totalDays - baseDays * cities.length;
    for (let i = 0; remainingDays > 0; i++, remainingDays--) {
      daysArray[i % cities.length]++;
    }
    return daysArray;
  });

  const [editableItinerary, setEditableItinerary] = useState([]);
  const [undoHistory, setUndoHistory] = useState([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleDeleteAttraction = useCallback(
    (dayIndex, placeId) => {
      setEditableItinerary((prev) => {
        const day = prev[dayIndex];
  
        if (!day?.places) {
          return prev;
        }
  
        const next =
          JSON.parse(JSON.stringify(prev));
  
        const updatedPlaces =
          next[dayIndex].places.filter(
            (place) =>
              getPlaceKey(place) !==
              String(placeId)
          );
  
        const validation =
          prepareAndValidateDay(updatedPlaces);
  
        if (!validation.ok) {
          alert(
            `Unable to delete attraction.\n\n${validation.message}`
          );
  
          return prev;
        }
  
        /*
         * Successful edit only:
         * create exactly one Undo snapshot.
         */
        setUndoHistory((history) => [
          ...history,
          JSON.parse(JSON.stringify(prev)),
        ]);
  
        next[dayIndex].places =
          validation.places;
  
        return next;
      });
    },
    []
  );
  
  useEffect(() => {
    const loadSavedItinerary = async () => {
      if (location.state?.tripData && location.state?.itineraryData) {
  setEditableItinerary(location.state.itineraryData);
  setTripData(location.state.tripData);
  setSavedTitle(location.state.savedTitle || '');
  setIsEditFromSaved(Boolean(location.state?.savedId || savedIdFromUrl));
  setExploreByCityRaw(location.state?.explore || []);
  return;
}
      
  
      if (savedIdFromUrl) {
        try {
          const response = await fetch(api.url(`/api/saved-itineraries/${savedIdFromUrl}`), {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          });
  
          if (!response.ok) throw new Error('Failed to load saved itinerary');
  
          const result = await response.json();
  
          setTripData(result.trip_data);
          setEditableItinerary(result.itinerary_data);
          setSavedTitle(result.title);
          setIsEditFromSaved(true);
        } catch  {
          
        }
      }
    };
  
    loadSavedItinerary();
  }, [location.state, savedIdFromUrl, accessToken]);
 
  // Scroll mobile itinerary to 10:00 (with retries until the node exists)
  useEffect(() => {
    const isMobile = typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false;
  
    if (!isMobile) return;
    if (activeTab !== "itinerary") return;
  
    // Wait for the layout to paint, then scroll the *mobile* grid so that
    // the 10:00 line sits right under the sticky day header.
    requestAnimationFrame(() => {
      const mark = tenAMRef.current;
      if (!mark) return;
  
      const scroller =
        mark.closest(".mobile-itinerary .calendar-grid") ||
        mark.closest(".calendar-grid");
  
      if (!scroller) {
        // Fallback: scroll the page if we can't find the intended scroller
        mark.scrollIntoView({ block: "start", behavior: "auto" });
        return;
      }
  
      const header = scroller.querySelector(".day-column .day-header");
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const extra = 8; // small breathing room
      scroller.scrollTop = Math.max(0, mark.offsetTop - headerHeight - extra);
    });
  }, [activeTab, selectedDayMobile, editableItinerary.length]);
  
  
  const regenerateItinerary = async (customDays) => {
    const daysToUse = customDays || daysPerDestination;
  
    const daysPerCity = tripData.destinations.reduce((acc, city, index) => {
      acc[city] = daysToUse[index];
      return acc;
    }, {});
    
   

    const tripTypeResolved = typeof tripData.tripType === "string"
  ? tripData.tripType
  : Array.isArray(tripData.tripType)
  ? tripData.tripType[0]
  : "tourist-attractions"; // ✅ fallback default

const tripRequest = {
  origin_city: tripData.origin,
  destinations: tripData.destinations,
  trip_types: [tripTypeResolved],
  pace: tripData.tripPace,
  start_date: tripData.startDate,
  end_date: tripData.endDate,
  daysPerCity
};

    try {
      setIsRegenerating(true);
      const headers = { "Content-Type": "application/json" };
if (isLoggedIn && accessToken) {
  headers.Authorization = `Bearer ${accessToken}`;
}

const response = await fetch(api.url('/api/itinerary/generate'), {
  method: "POST",
  headers,
  body: JSON.stringify(tripRequest),
});

    
      
      const rawText = await response.text();
      
    
      if (!response.ok) throw new Error("Failed to regenerate itinerary");
    
      const data = JSON.parse(rawText);
setEditableItinerary(data.itinerary);

if (Array.isArray(data.explore)) {
  setExploreByCityRaw(data.explore);
} else {
  setExploreByCityRaw([]);
}

    } catch  {
      
      alert("Failed to update itinerary based on new day count.");
    }
    finally {
      setIsRegenerating(false);
    }
  };

  const adjustDays = (index, change) => {
    if (tripData.destinations.length < 2) return;
  
    const newDays = [...daysPerDestination];
    const currentDayValue = newDays[index];
    if (currentDayValue + change < 1) return;
  
    for (let i = 0; i < newDays.length; i++) {
      if (i !== index && newDays[i] - change >= 1) {
        newDays[i] -= change;
        newDays[index] += change;
  
        setDaysPerDestination(newDays);     // update the visual
        setHasPendingChanges(true);         // ✅ show the confirm button
        return;
      }
    }
  };
  function adjustAndValidateInsertion(existing, newPlace) {
    const combined = [...existing, newPlace].sort((a, b) => a.start_time - b.start_time);
    const updated = [];
  
    for (let i = 0; i < combined.length; i++) {
      const current = { ...combined[i] };
  
      if (i === 0) {
        current.travel_time_from_previous = "0 min";
        
      } else {
        const prev = updated[i - 1];
        const travel = getTravelTimeBetween(prev, current);
        const expectedStart = prev.start_time + prev.visit_duration + travel / 60;
  
        if (current.id === newPlace.id) {
          // Only apply adjustment logic to the newly added attraction
          const gapBeforeNext = i + 1 < combined.length
            ? combined[i + 1].start_time - expectedStart
            : 24 - expectedStart;
  
          if (gapBeforeNext * 60 < current.visit_duration * 60) {
            const adjustedDuration = Math.max(0.5, gapBeforeNext);
            const proceed = window.confirm(
              `⚠️ Not enough time for this attraction before the next one.\nDo you want to proceed with shortened duration of ${Math.round(adjustedDuration * 60)} mins?`
            );
            if (!proceed) return null;
            current.visit_duration = adjustedDuration;
          }
  
          current.start_time = expectedStart;
          current.travel_time_from_previous = formatTravelTime(travel);
        } else {
          // Preserve original start time and shift travel time
         
          current.travel_time_from_previous = formatTravelTime(travel);
        }
  
        current.start_time = Math.round(current.start_time * 2) / 2;
      }
  
      if (current.start_time + current.visit_duration > 24) {
        alert("⚠️ Cannot schedule attraction. Day ends at midnight.");
        return null;
      }
  
      updated.push(current);
    }
  
    return updated;
  }
  const moveCard = useCallback(
    (
      fromDay,
      toDay,
      fromIndex,
      cardData,
      newStartTime
    ) => {
      setEditableItinerary((prev) => {
        const targetDay =
          prev[toDay];
  
        if (!targetDay) {
          alert("Please choose a valid day.");
          return prev;
        }
  
        if (
          !Number.isFinite(
            Number(newStartTime)
          )
        ) {
          alert("Please choose a valid time.");
          return prev;
        }
  
        const selectedStart =
          Number(newStartTime);
  
        const duration =
          Number(
            cardData.visit_duration ?? 2
          );
  
        if (
          selectedStart < 0 ||
          selectedStart >= 24
        ) {
          alert(
            "Please choose a valid start time."
          );
  
          return prev;
        }
  
        if (
          selectedStart + duration >
          24 + 1e-6
        ) {
          alert(
            `${cardData.name} would end after midnight. Please choose an earlier time or a shorter duration.`
          );
  
          return prev;
        }
  
        /*
         * Existing itinerary move:
         * source and destination must remain same city.
         *
         * Explore adds use fromDay === -1.
         */
        if (fromDay >= 0) {
          const sourceDay =
            prev[fromDay];
  
          if (
            sourceDay?.city &&
            targetDay?.city &&
            sourceDay.city !==
              targetDay.city
          ) {
            alert(
              "You cannot move an attraction to a different city."
            );
  
            return prev;
          }
        }
  
        const next =
          JSON.parse(JSON.stringify(prev));
  
        const cardKey =
          getPlaceKey(cardData);
  
        /*
         * Remove from source when this is an existing
         * scheduled attraction.
         */
        if (fromDay >= 0) {
          next[fromDay].places =
            next[fromDay].places.filter(
              (place) =>
                getPlaceKey(place) !==
                cardKey
            );
        }
  
        /*
         * Also prevent duplicate copies in destination.
         */
        next[toDay].places =
          next[toDay].places.filter(
            (place) =>
              getPlaceKey(place) !==
              cardKey
          );
  
        const newCard = {
          ...cardData,
          start_time: selectedStart,
          visit_duration: duration,
        };
  
        next[toDay].places.push(newCard);
  
        /*
         * Same-day move:
         * one full-day validation.
         */
        if (
          fromDay >= 0 &&
          fromDay === toDay
        ) {
          const result =
            prepareAndValidateDay(
              next[toDay].places
            );
  
          if (!result.ok) {
            alert(
              `Time conflict.\n\n${result.message}`
            );
  
            return prev;
          }
  
          next[toDay].places =
            result.places;
  
          setUndoHistory((history) => [
            ...history,
            JSON.parse(JSON.stringify(prev)),
          ]);
  
          return next;
        }
  
        /*
         * Different-day move:
         * source day also needs travel recalculated.
         */
        if (fromDay >= 0) {
          const sourceResult =
            prepareAndValidateDay(
              next[fromDay].places
            );
  
          if (!sourceResult.ok) {
            alert(
              `Unable to move attraction.\n\n${sourceResult.message}`
            );
  
            return prev;
          }
  
          next[fromDay].places =
            sourceResult.places;
        }
  
        /*
         * Validate destination day.
         *
         * This also handles Explore additions
         * because fromDay === -1.
         */
        const destinationResult =
          prepareAndValidateDay(
            next[toDay].places
          );
  
        if (!destinationResult.ok) {
          alert(
            `Time conflict.\n\n${destinationResult.message}`
          );
  
          return prev;
        }
  
        next[toDay].places =
          destinationResult.places;
  
        /*
         * ONLY successful operations reach here.
         */
        setUndoHistory((history) => [
          ...history,
          JSON.parse(JSON.stringify(prev)),
        ]);
  
        return next;
      });
    },
    []
  );

  const handleSaveItinerary = async () => {
    if (isEditMode) {
      alert("Please exit Edit Mode before saving your itinerary!");
      return;
    }    
    
    if (!isLoggedIn || !accessToken) {
      alert('You must be logged in to save your itinerary!');
      navigate('/login');
      return;
    }
  
    try {
      const title = (
        isEditFromSaved && savedTitle?.trim()
          ? savedTitle.trim()
          : (prompt("Enter a name for your trip:") || "").trim()
      );
      
      if (!title) {
        alert("Trip name is required to save!");
        return;
      }
      

      await fetch(api.url('/api/saved-itineraries/save-full'), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        tripData,
        editableItinerary,
        title,
        saved_id: isEditFromSaved ? savedId : undefined,
      }),
    });

    navigate("/my-itineraries");
  } catch (error) {
   
    alert(error.message || "Failed to save itinerary. Please try again.");
  }
};
  
   
 const handleSearchAttractions = () => {
  if (searchQuery.trim() === "") {
    setLiveSearchResults([]);
    return;
  }

  setIsSearchingLive(true);

  fetch(
    api.url(`/api/places/search-attraction?query=${encodeURIComponent(searchQuery)}`)
  )
    .then((res) => res.json())
    .then((data) => {
      setLiveSearchResults(data);
    })
    .catch(() => {
      setLiveSearchResults([]);
    })
    .finally(() => setIsSearchingLive(false));
};
  
  const scrollRef = useRef();

  useEffect(() => {
    if (scrollRef.current) {
      const scrollToHour = 19;
      const pixelsPerHalfHour = 25;
      const scrollTop = scrollToHour * 2 * pixelsPerHalfHour; // 10:00 AM → 20 * 25 = 500
      scrollRef.current.scrollTop = scrollTop;
    }
  }, []);
  // ✅ Derived Explore data (filtered) — items come back on undo/delete
const exploreByCity = React.useMemo(() => {
  if (!Array.isArray(exploreByCityRaw)) return [];

  const getPlaceKey = (p) =>
    String(
      p?.id ??
      p?.place_id ??
      p?.google_place_id ??
      p?.placeId ??
      ""
    );

  const scheduled = new Set(
    editableItinerary.flatMap(day =>
      (day.places || [])
        .map(getPlaceKey)
        .filter(k => k && k !== "undefined" && k !== "null")
    )
  );

  const keepIfNotScheduled = (p) => {
    const k = getPlaceKey(p);
    if (!k || k === "undefined" || k === "null") return true;
    return !scheduled.has(k);
  };

  const normalized = exploreByCityRaw
  .map((cityBlock) => {
    if (!cityBlock) return null;

    const places = (cityBlock.places || []).filter(keepIfNotScheduled);

    return {
      ...cityBlock,
      places,
      sections: [],
    };
  })
  .filter(Boolean)
  .filter((cityBlock) => (cityBlock.places || []).length > 0);

  console.log("🧭 FRONTEND exploreByCity derived:", normalized);

  return normalized;
}, [editableItinerary, exploreByCityRaw]);

 // NEW: render one day column (used in mobile view)
const renderSingleDayColumn = (dayData, dayIndex) => (
  <DayColumn
    key={dayIndex}
    dayData={dayData}
    dayIndex={dayIndex}
    moveCard={moveCard}
    allPlaces={dayData.places}
    totalDays={totalDays}
    editableItinerary={editableItinerary}
    setEditableItinerary={setEditableItinerary}
    handleDeleteAttraction={handleDeleteAttraction}
    setUndoHistory={setUndoHistory}
    isEditMode={isEditMode}
  />
);

  return (
    <div className="itinerary-page">
      <div className="header">
            <div className="brand" onClick={() => navigate("/")}>
            <img src="/images/customroam-logo.png" alt="Logo" className="logo-img" />
            <span className="brand-text">CUSTOM ROAM</span>
             </div>
              {/* Hamburger menu button */}
              <button className="header-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                ☰
               </button>
             <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <span className="nav-link" onClick={() => navigate('/')}>Home</span>
                    <span className="nav-link" onClick={() => navigate('/roam-share')}>Roam Share</span>
                    <span className="nav-link" onClick={() => navigate('/my-itineraries')}>My Itineraries</span>
                    <span className="notification-icon" onClick={() => navigate('/notifications')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                       height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                     className="bell-svg"
                    >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                         <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                 </span>
                    <span className="nav-link" onClick={() => navigate('/profile')}>Profile</span>
                </div>
            </div>
       {/* NEW: Mobile tabs (hidden on desktop via CSS) */}
<nav className="tabs-mobile">
  <button
    className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
    onClick={() => setActiveTab("overview")}
  >
    Overview
  </button>
  <button
    className={`tab-btn ${activeTab === "itinerary" ? "active" : ""}`}
    onClick={() => setActiveTab("itinerary")}
  >
    Itinerary
  </button>
  <button
    className={`tab-btn ${activeTab === "explore" ? "active" : ""}`}
    onClick={() => setActiveTab("explore")}
  >
    Explore
  </button>
</nav>

      <div className="itinerary-layout" >
        {/* Route Overview */}
        <section className="route-overview tab-panel" data-tab="overview" data-active={activeTab === "overview"} >
          <h2>Route Overview</h2>
          <div className="route-flow">
            <div className="route-step">
              <div className="dot" />
              <span>{tripData.origin}</span>
            </div>
            {tripData.destinations?.map((city, i) => (
              <React.Fragment key={i}>
                <div className="route-line" />
                <div className="flight-icon">✈️</div>
                <div className="route-line" />
                <div className="route-step">
                  <div className="dot" />
                  <span>{city}</span>
                  <div className="days-container">
                    {tripData.destinations.length > 1 ? (
                      <>
                        <button className="arrow-button" onClick={() => adjustDays(i, 1)}>▲</button>
                        <span className="days-box">{daysPerDestination[i]}</span>
                        <button className="arrow-button" onClick={() => adjustDays(i, -1)}>▼</button>
                      </>
                    ) : (
                      <span className="days-box">{daysPerDestination[i]}</span>
                    )}
                    <span className="days-text">days</span>
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className="route-line" />
            <div className="flight-icon">✈️</div>
            <div className="route-line" />
            <div className="route-step">
              <div className="dot" />
              <span>{tripData.origin}</span>
            </div>
          </div>
          {hasPendingChanges && (
          <div className="confirm-button-container">
          <button
          className="confirm-button"
          onClick={() => {
          regenerateItinerary(daysPerDestination);  // regenerate on confirm
          setHasPendingChanges(false);              // hide button
          }}
          >
          Confirm & Update Itinerary
          </button>
          </div>
          )}
          </section>
          
        
         {/* Day-by-Day Itinerary */}
         
         <section
  className="day-by-day-section tab-panel"
  data-tab="itinerary"
  data-active={activeTab === "itinerary"}
>
<div className="day-by-day-header">
  <h2>Day-by-Day Itinerary</h2>
  <div className="header-buttons desktop-only">
      {!isEditMode && (
        <button onClick={handleSaveItinerary} className="save-itinerary-btn">
          Save to My Itineraries
        </button>
      )}

      <button onClick={() => setIsEditMode((prev) => !prev)} className="edit-itinerary-btn">
        {isEditMode ? 'Exit Edit Mode' : 'Edit Itinerary'}
      </button>

      {isEditMode && (
        <button
          onClick={() => {
            if (undoHistory.length === 0) return;
            const previous = undoHistory[undoHistory.length - 1];
            setEditableItinerary(previous);
            setUndoHistory((h) => h.slice(0, -1));
          }}
          disabled={undoHistory.length === 0}
          className="undo-btn"
        >
          ⟲ Undo
        </button>
      )}
    </div>
  </div>
 {/* Mobile action buttons (always visible on mobile) */}
<div className="mobile-action-bar mobile-only">
  {/* SAVE — always shown; disabled while editing */}
  <button
    type="button"
    className="fab fab-save"
    onClick={handleSaveItinerary}
    disabled={isEditMode}
    aria-label="Save itinerary"
    title={isEditMode ? "Exit Edit Mode to Save" : "Save itinerary"}
  >
    💾
  </button>

  {/* EDIT / EXIT + UNDO */}
  {!isEditMode ? (
    <button
      type="button"
      className="fab fab-edit"
      onClick={() => setIsEditMode(true)}
      aria-label="Edit itinerary"
      title="Edit itinerary"
    >
      ✏️
    </button>
  ) : (
    <>
      <button
        type="button"
        className="fab fab-exit"
        onClick={() => setIsEditMode(false)}
        aria-label="Exit edit mode"
        title="Exit edit mode"
      >
        ✖️
      </button>
      <button
        type="button"
        className="fab fab-undo"
        onClick={() => {
          if (undoHistory.length === 0) return;
          const previous = undoHistory[undoHistory.length - 1];
          setEditableItinerary(previous);
          setUndoHistory((h) => h.slice(0, -1));
        }}
        disabled={undoHistory.length === 0}
        aria-label="Undo"
        title="Undo"
      >
        ⟲
      </button>
    </>
  )}
</div>

  {isRegenerating && <p className="loading-message">Updating itinerary...</p>}

  {/* DESKTOP grid (unchanged), hidden on mobile */}
  <div className="calendar-wrapper desktop-only" ref={scrollRef}>
    <div className="calendar-grid">
    <div className="calendar-column time-column">
  <div className="cell time-header"></div>
  {Array.from({ length: 48 }).map((_, i) => {
    const hour = TIME_START + Math.floor(i / 2);
    const minutes = i % 2 === 0 ? "00" : "30";
    const isTenAM = hour === 10 && minutes === "00";

    return (
      <div
        key={i}
        className="cell time-slot"
        ref={isTenAM ? tenAMRef : undefined}   // ← keep this on MOBILE time column
      >
        {`${hour.toString().padStart(2, "0")}:${minutes}`}
      </div>
    );
  })}
</div>
      {editableItinerary.map((dayData, dayIndex) => (
        <DayColumn
          key={dayIndex}
          dayData={dayData}
          dayIndex={dayIndex}
          moveCard={moveCard}
          allPlaces={dayData.places}
          totalDays={totalDays}
          editableItinerary={editableItinerary}
          setEditableItinerary={setEditableItinerary}
          handleDeleteAttraction={handleDeleteAttraction}
          setUndoHistory={setUndoHistory}
          isEditMode={isEditMode}
        />
      ))}
    </div>
  </div>

  {/* MOBILE itinerary: one day at a time */}
  <div className="mobile-itinerary mobile-only">
    {/* Horizontal day pill bar */}
    <div className="day-pill-bar">
      {editableItinerary.map((d, idx) => (
        <button
          key={idx}
          className={`day-pill ${selectedDayMobile === idx ? "active" : ""}`}
          onClick={() => setSelectedDayMobile(idx)}
        >
          Day {d.day}
        </button>
      ))}
    </div>

    {/* Time column + selected day column */}
    <div className="calendar-grid">
      <div className="calendar-column time-column">
        <div className="cell time-header"></div>
        {Array.from({ length: 48 }).map((_, i) => {
  const hour = TIME_START + Math.floor(i / 2);
  const minutes = i % 2 === 0 ? "00" : "30";
  const isTenAM = hour === 10 && minutes === "00";

  return (
    <div
      key={i}
      className="cell time-slot"
      ref={isTenAM ? tenAMRef : null}   // ← attach ref to 10:00
    >
      {`${hour.toString().padStart(2, "0")}:${minutes}`}
    </div>
  );
})}

      </div>

      {editableItinerary[selectedDayMobile] &&
        renderSingleDayColumn(editableItinerary[selectedDayMobile], selectedDayMobile)}
    </div>
  </div>
</section>      
      </div>
      <section className="explore-section tab-panel" data-tab="explore" data-active={activeTab === "explore"}>
      <div className="explore-header">
  <h2>Explore</h2>
</div>
{isLoadingExplore && (
  <div className="searching-status">Loading explore places...</div>
)}

{exploreError && (
  <div className="searching-status" style={{ color: "red" }}>
    {exploreError}
  </div>
)}

{!isLoadingExplore && !exploreError && (
  !Array.isArray(exploreByCity) ||
  exploreByCity.every((block) => {
    const placesCount = Array.isArray(block.places) ? block.places.length : 0;
    const sectionsCount = Array.isArray(block.sections)
      ? block.sections.reduce((sum, sec) => sum + ((sec.places || []).length), 0)
      : 0;

    return placesCount === 0 && sectionsCount === 0;
  })
) && (
  <div className="searching-status">
    No extra attractions found for Explore (or all are already scheduled).
  </div>
)}

      {isEditMode && (
  <div className="explore-search-input-wrapper" >
    <span className="explore-search-icon">🔍</span>
    <input
      type="text"
      placeholder="Search attractions..."
      value={searchQuery}
      onChange={(e) => {
        setSearchQuery(e.target.value);
        setLiveSearchResults([]);
      }}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck={false}
      className="explore-search-input"
    />
    <button onClick={handleSearchAttractions} className="explore-search-button">
  Search
</button>

  </div>
)}


{isSearchingLive && (
  <div className="searching-status">
    Searching attractions...
  </div>
)}

  {searchQuery.trim() !== "" && isEditMode ? (
    // LIVE SEARCH RESULTS
    <div className="explore-grid">
      {liveSearchResults.map((place, i) => (
        <div key={`${place.name}-${i}`} className="explore-card">
          <div className="explore-place-name">
           {place.name}
        </div>
        {place.rating && (
  <p className="explore-rating">
    ⭐ {place.rating.toFixed(1)}{" "}
    <span className="explore-review-count">
      ({place.reviews?.toLocaleString() || 0} reviews)
    </span>
  </p>
)}

<p className="explore-address">{place.address}</p>
<p className="explore-duration">Duration: {place.visit_duration} hrs</p>


{isEditMode && (
  <div className="explore-edit-wrapper">
    <div className="explore-move-row">
      <label className="explore-label">Move to:</label>

      <select
        value={place.selectedDay ?? 0}
        onChange={(e) => {
          const newDay = parseInt(e.target.value);
          const updated = [...liveSearchResults];
          updated[i].selectedDay = newDay;
          setLiveSearchResults(updated);
        }}
        className="explore-select"
      >
        {Array.from({ length: totalDays }).map((_, i) => (
          <option key={i} value={i}>{`Day ${i + 1}`}</option>
        ))}
      </select>

      <select
        value={place.selectedTime ?? 10}
        onChange={(e) => {
          const newTime = parseFloat(e.target.value);
          const updated = [...liveSearchResults];
          updated[i].selectedTime = newTime;
          setLiveSearchResults(updated);
        }}
        className="explore-select"
      >
        {Array.from({ length: 48 }, (_, i) => {
          const hour = Math.floor(i / 2);
          const minutes = i % 2 === 0 ? "00" : "30";
          return (
            <option key={i} value={i * 0.5}>
              {`${hour.toString().padStart(2, "0")}:${minutes}`}
            </option>
          );
        })}
      </select>
    </div>

    <div className="explore-move-row">
      <label className="explore-label">Duration:</label>
      <select
        value={place.visit_duration}
        onChange={(e) => {
          const newDuration = parseFloat(e.target.value);
          const updated = [...liveSearchResults];
          updated[i].visit_duration = newDuration;
          setLiveSearchResults(updated);
        }}
        className="explore-select"
      >
        {[...Array(12)].map((_, i) => {
          const val = (i + 1) * 0.5;
          return <option key={val} value={val}>{val} hrs</option>;
        })}
      </select>

      <button
        onClick={() => {
          const day = place.selectedDay ?? 0;
const time = parseFloat(place.selectedTime ?? 10);

if (place.latitude == null || place.longitude == null) {
  alert("Missing coordinates. Please try a different place.");
  return;
}
          

          const placeWithCoords = {
            ...place,
            coordinates: {
              latitude: place.latitude,
              longitude: place.longitude
            }
          };

          moveCard(-1, day, -1, placeWithCoords, time);

          
        }}
        className="explore-move-btn"
      >
        Move
      </button>
    </div>
  </div>
)}

        </div>
      ))}
    </div>
  ) : (
    <div className="explore-city-groups">
  {exploreByCity.map((block) => {
  if (!block) return null;

  return (
    <div key={block.city} className="explore-city-group">
      <h3 className="explore-city-heading">{block.city}</h3>

      <div className="explore-grid">
        {(block.places || []).map((place) => (
          <div
            key={place.id || `${place.name}-${place.address}`}
            className="explore-card"
          >
            <div className="explore-place-name">{place.name}</div>

            {!!place.rating && (
              <p className="explore-rating">
                ⭐ {Number(place.rating).toFixed(1)}{" "}
                <span className="explore-review-count">
                  ({place.reviews?.toLocaleString?.() || place.reviews || 0} reviews)
                </span>
              </p>
            )}

            <p className="explore-address">{place.address}</p>
            <p className="explore-duration">
              Duration: {place.visit_duration ?? 2} hrs
            </p>

            {isEditMode && (
              <div className="explore-edit-wrapper">
                <div className="explore-move-row">
                  <label className="explore-label">Move to:</label>

                  <select
                    value={place.selectedDay ?? 0}
                    onChange={(e) => {
                      const newDay = parseInt(e.target.value);
                      setExploreByCityRaw((prev) =>
                        prev.map((b) => {
                          if (b.city !== block.city) return b;
                          return {
                            ...b,
                            places: (b.places || []).map((p) =>
                              p.id === place.id ? { ...p, selectedDay: newDay } : p
                            ),
                          };
                        })
                      );
                    }}
                    className="explore-select"
                  >
                    {Array.from({ length: totalDays }).map((_, i) => (
                      <option key={i} value={i}>{`Day ${i + 1}`}</option>
                    ))}
                  </select>

                  <select
                    value={place.selectedTime ?? 10}
                    onChange={(e) => {
                      const newTime = parseFloat(e.target.value);
                      setExploreByCityRaw((prev) =>
                        prev.map((b) => {
                          if (b.city !== block.city) return b;
                          return {
                            ...b,
                            places: (b.places || []).map((p) =>
                              p.id === place.id ? { ...p, selectedTime: newTime } : p
                            ),
                          };
                        })
                      );
                    }}
                    className="explore-select"
                  >
                    {Array.from({ length: 48 }, (_, i) => {
                      const hour = Math.floor(i / 2);
                      const minutes = i % 2 === 0 ? "00" : "30";
                      return (
                        <option key={i} value={i * 0.5}>
                          {`${hour.toString().padStart(2, "0")}:${minutes}`}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="explore-move-row">
                  <label className="explore-label">Duration:</label>

                  <select
                    value={place.visit_duration ?? 2}
                    onChange={(e) => {
                      const newDuration = parseFloat(e.target.value);
                      setExploreByCityRaw((prev) =>
                        prev.map((b) => {
                          if (b.city !== block.city) return b;
                          return {
                            ...b,
                            places: (b.places || []).map((p) =>
                              p.id === place.id ? { ...p, visit_duration: newDuration } : p
                            ),
                          };
                        })
                      );
                    }}
                    className="explore-select"
                  >
                    {[...Array(12)].map((_, i) => {
                      const val = (i + 1) * 0.5;
                      return <option key={val} value={val}>{val} hrs</option>;
                    })}
                  </select>

                  <button
                    onClick={() => {
                      const day = place.selectedDay ?? 0;
                      const time = place.selectedTime ?? 10;
                      const targetCity = editableItinerary[day]?.city;

                      if (targetCity && place.city !== targetCity) {
                        alert("You cannot add an attraction from another city into this day.");
                        return;
                      }

                      const coords =
                        place.coordinates ||
                        (place.latitude && place.longitude
                          ? { latitude: place.latitude, longitude: place.longitude }
                          : null);

                      if (!coords) {
                        alert("Missing coordinates for this place.");
                        return;
                      }

                      moveCard(-1, day, -1, { ...place, coordinates: coords }, time);
                      
                    }}
                    className="explore-move-btn"
                  >
                    Move
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
})}
</div>
  )}
  
</section>
{/* NEW: Mobile FABs (hidden on desktop) */}
{/* NEW: Mobile FABs (hidden on desktop) */}
{activeTab !== 'itinerary' && (
  <div className="fab-cluster mobile-only">
    {!isEditMode ? (
      <>
        {/* Hide EDIT only on Overview */}
        {activeTab !== 'overview' && (
          <button
            className="fab"
            onClick={() => setIsEditMode(true)}
            aria-label="Edit itinerary"
            title="Edit itinerary"
          >
            ✏️
          </button>
        )}

        {/* Keep SAVE everywhere */}
        <button
          className="fab primary"
          onClick={handleSaveItinerary}
          aria-label="Save itinerary"
          title="Save itinerary"
        >
          💾
        </button>
      </>
    ) : (
      // If the user is in edit mode, still show exit/undo on any tab
      <>
        <button
          className="fab"
          onClick={() => setIsEditMode(false)}
          aria-label="Exit edit mode"
          title="Exit edit mode"
        >
          ✕
        </button>
        <button
          className="fab"
          disabled={undoHistory.length === 0}
          onClick={() => {
            if (undoHistory.length === 0) return;
            const previous = undoHistory[undoHistory.length - 1];
            setEditableItinerary(previous);
            setUndoHistory((h) => h.slice(0, -1));
          }}
          aria-label="Undo"
          title="Undo"
        >
          ⟲
        </button>
      </>
    )}
  </div>
)}

        </div>
    );
}
 
export default ItineraryPage;
